import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useAlert } from "../../shared/components/AlertProvider";
import Card from "../../shared/components/Card";
import Flex from "../../shared/components/Flex";
import Input from "../../shared/components/Input";
import ModalButton from "../../shared/components/ModalButton";
import Padding from "../../shared/components/Padding";
import Select from "../../shared/components/Select";
import Spacer from "../../shared/components/Spacer";
import Text from "../../shared/components/Text";
import useDevice from "../../shared/hooks/useDevice";
import { responseMsg } from "../common/ResponseMsg";
import ScrollModal from "../common/ScrollModal";
import CusFullCalendar from "./CusFullCalendar";
import "./holidayStyle.css";
import Loading from "../../shared/components/Loading";
import useDelayedLoading from "../../shared/hooks/useDelayedLoading";

type HolidayProps = {
    name?: string;
};

const NON_SELECT_OPTION = { text: "미선택", value: "-1", default: true };

function Holiday({ name }: HolidayProps) {
    const [regionCodeList, setRegionCodeList] = useState<any[]>([]);
    const [selectRegionLv1, setSelectRegionLv1] = useState<string>("");
    const [selectRegionLv2, setSelectRegionLv2] = useState<string>(NON_SELECT_OPTION.value);
    const [selectRegionLv3, setSelectRegionLv3] = useState<string>(NON_SELECT_OPTION.value);
    const [holidayList, setHolidayList] = useState<any[]>([]);
    const openAlert = useAlert();
    const [modalState, setModalState] = useState<{
        open: boolean;
        mode: "add" | "remove";
        date: string;
        memo: string;
        existingMemo?: string;
        regionId?: string;
    }>({
        open: false,
        mode: "add",
        date: "",
        memo: "",
        existingMemo: "",
        regionId: undefined,
    });
    const device = useDevice();
    const isMobile = device === "mobile";

    const level1Options = useMemo(
        () =>
            regionCodeList
                .filter((code) => code.depth === 1)
                .map((ele, index) => ({
                    text: "대한민국",
                    value: String(ele.id),
                    default: index === 0,
                })),
        [regionCodeList]
    );

    const level2Options = useMemo(
        () => [
            NON_SELECT_OPTION,
            ...regionCodeList
                .filter((code) => code.depth === 2)
                .map((ele) => ({
                    text: ele.name,
                    value: String(ele.id),
                })),
        ],
        [regionCodeList]
    );

    const level3Options = useMemo(
        () => [
            NON_SELECT_OPTION,
            ...regionCodeList
                .filter((code) => code.depth === 3 && String(code.parent) === selectRegionLv2)
                .map((ele) => ({
                    text: ele.name,
                    value: String(ele.id),
                })),
        ],
        [regionCodeList, selectRegionLv2]
    );

    const getHoliday = (regionCodes: any[]) => {
        let lvCode: any;

        if (selectRegionLv3 !== NON_SELECT_OPTION.value) {
            lvCode = regionCodes.find((code) => String(code.id) === selectRegionLv3);
        } else if (selectRegionLv2 !== NON_SELECT_OPTION.value) {
            lvCode = regionCodes.find((code) => String(code.id) === selectRegionLv2);
        } else {
            lvCode = regionCodes.find((code) => code.depth === 1);
        }

        if (!lvCode) return;

        fetch(`/api/holiday?region=${lvCode.id}`, {
            method: "get",
        })
            .then((res) => res.json())
            .then((json) => {
                setHolidayList(json.data);
            });
    };

    useEffect(() => {
        fetch("/api/system/codes/REGION", {
            method: "GET",
        })
            .then((res) => res.json())
            .then((json) => {
                if (json.code === responseMsg.SUCCESS.code) {
                    const lv1 = json.data.find((ele: any) => ele.depth === 1);

                    setRegionCodeList(json.data);
                    if (lv1) {
                        setSelectRegionLv1(String(lv1.id));
                    }
                } else if (json.code === responseMsg.FAIL.code) {
                    openAlert({ text: "지역 정보를 불러오지 못했습니다.", type: "error" });
                }
            })
            .catch(() => {
                openAlert({ text: "지역 정보를 불러오지 못했습니다.", type: "error" });
            });
    }, []);

    useEffect(() => {
        setSelectRegionLv3(NON_SELECT_OPTION.value);
    }, [selectRegionLv2]);

    useEffect(() => {
        if (regionCodeList.length) {
            getHoliday(regionCodeList);
        }
    }, [regionCodeList, selectRegionLv1, selectRegionLv2, selectRegionLv3]);

    const handleDateClick = (event: any, date: string) => {
        if (selectRegionLv2 === NON_SELECT_OPTION.value && selectRegionLv3 === NON_SELECT_OPTION.value) {
            openAlert({ text: "대한민국 공휴일은 수정할 수 없습니다.", type: "warning" });
            return;
        }

        const existingHoliday = holidayList.find((item) => item.holiday === date || item.holidayDate === date);
        const useYn = existingHoliday ? "N" : "Y";

        let selectCode: string;

        if (selectRegionLv3 !== NON_SELECT_OPTION.value) {
            selectCode = selectRegionLv3;
        } else if (selectRegionLv2 !== NON_SELECT_OPTION.value) {
            selectCode = selectRegionLv2;
        } else {
            selectCode = selectRegionLv1;
        }

        setModalState({
            open: true,
            mode: useYn === "Y" ? "add" : "remove",
            date,
            memo: "",
            existingMemo: existingHoliday?.memo,
            regionId: selectCode,
        });
    };

    const closeModal = () => {
        setModalState((prev) => ({ ...prev, open: false, memo: "" }));
    };

    const submitHoliday = () => {
        if (!modalState.regionId) return;

        fetch("/api/holiday", {
            method: "post",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                holiday: modalState.date,
                region: modalState.regionId,
                useYn: modalState.mode === "add" ? "Y" : "N",
                memo: modalState.mode === "add" ? modalState.memo : modalState.existingMemo,
            }),
        })
            .then((res) => res.json())
            .then(() => {
                openAlert({
                    text: modalState.mode === "add" ? "공휴일을 추가했습니다." : "공휴일을 삭제했습니다.",
                    type: "info",
                });
                closeModal();
                getHoliday(regionCodeList);
            })
            .catch(() => {
                openAlert({
                    text: "요청을 처리하지 못했습니다. 잠시 후 다시 시도해주세요.",
                    type: "error",
                });
            });
    };

    const modalHeader = modalState.mode === "add" ? "일정 등록" : "일정 삭제";
    const modalMessage = modalState.mode === "add" ? "일정을 등록하시겠습니까?" : "해당 공휴일을 삭제할까요?";
    const modalButtonText = modalState.mode === "add" ? "등록" : "삭제";

    const modalContent = (
        <Padding paddingX={24} paddingY={24}>
            <Card
                padding={25}
                border={0}
                shadow={0}
                style={{
                    backgroundColor: "#F5F5F5",
                }}
            >
                <Flex flexDirection="column" alignItems="center" gap={10}>
                    <div
                        style={{
                            width: 46,
                            height: 46,
                            borderRadius: 28,
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <img src="/images/ico_stn_calendar.png" alt="calendar" style={{ width: 20, height: 22 }} />
                    </div>
                    <Text size={24} weight="semibold" color="#EE7D00">
                        {modalState.date ? moment(modalState.date).format("YYYY.MM.DD") : ""}
                    </Text>
                    <Text size={18} color="#555555">
                        {modalMessage}
                    </Text>
                </Flex>
            </Card>

            <Spacer direction="vertical" size={16} />

            {modalState.mode === "add" ? (
                <Input
                    fullWidth
                    placeholder="일정을 입력해 주세요"
                    value={modalState.memo}
                    onChange={(e) => setModalState((prev) => ({ ...prev, memo: e.target.value }))}
                    style={{ borderRadius: 10 }}
                />
            ) : (
                modalState.existingMemo && (
                    <Card padding={14} shadow={0} style={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
                        <Text size={14} color="#222222">
                            {modalState.existingMemo}
                        </Text>
                    </Card>
                )
            )}

            <Spacer direction="vertical" size={16} />
            <Flex flexDirection={isMobile ? "column" : "row"} fullWidth justifyContent="end" gap={10}>
                <ModalButton
                    type={modalState.mode === "add" ? "insert" : "delete"}
                    text={modalButtonText}
                    onClick={submitHoliday}
                />
                <ModalButton
                    type="cancel"
                    text="취소"
                    onClick={closeModal}
                />
            </Flex>
        </Padding>
    );
    
    const isLoadingComponent = useDelayedLoading(500);

    return (
        <>
        <Loading isLoading={isLoadingComponent} minDuration={300} message="Loading..."/>
        <Padding padding={isMobile ? 6 : 32}>
            <Text textAlign="center" size={isMobile ? "20px" : "40px"} weight="bold" style={{userSelect: "none"}}>
                {name || "공휴일 관리"}
            </Text>
            <Spacer direction="vertical" size={24} />
            <Flex flexDirection="column" alignItems="center" gap={16}>
                <Card padding={35} paddingX={isMobile ? 10 : 60} style={{ maxWidth: 900, width: "100%", userSelect: "none" }}>
                    <Flex gap={12} justifyContent="center" noWrap={true}>
                        <Select
                            fullWidth
                            name="region-lv1"
                            options={level1Options}
                            onChange={(value) => setSelectRegionLv1(value)}
                        />
                        <Select
                            fullWidth
                            name="region-lv2"
                            options={level2Options}
                            onChange={(value) => setSelectRegionLv2(value)}
                        />
                        <Select
                            fullWidth
                            name="region-lv3"
                            options={level3Options}
                            onChange={(value) => setSelectRegionLv3(value)}
                        />
                    </Flex>
                    <Spacer direction="vertical" size={18} />
                    <CusFullCalendar mode="U" holidayList={holidayList} dateClick={handleDateClick} />
                </Card>
            </Flex>
            <ScrollModal
                size={"595px"}
                modal={{
                    open: modalState.open,
                    close: closeModal,
                    headerText: modalHeader,
                    main: modalContent,
                }} headerText={undefined} paddingX={undefined} paddingY={undefined} maxWidth={undefined}/>
        </Padding>
        </>
    );
}

export default Holiday;
